import { useEffect, useState } from "react";
import { FiDownload, FiLock, FiX } from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const statusClass = (status) => {
  if (status === "VERIFIED") return "bg-green-100 text-green-700";
  if (status === "REJECTED") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
};

const title = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function SensitiveEmployeeDetailsModal({
  employee,
  accessToken,
  canViewDocuments,
  canViewBank,
  onClose,
}) {
  const [documents, setDocuments] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!employee) return;

    let active = true;
    setLoading(true);
    setError("");

    const requests = [];
    if (canViewDocuments) {
      requests.push(
        apiRequest(`/employees/${employee.id}/sensitive-documents`, {
          accessToken,
        }).then((data) => ({ type: "documents", data })),
      );
    }
    if (canViewBank) {
      requests.push(
        apiRequest(`/employees/${employee.id}/bank-details`, {
          accessToken,
        }).then((data) => ({ type: "bank", data })),
      );
    }

    Promise.all(requests)
      .then((results) => {
        if (!active) return;
        results.forEach((result) => {
          if (result.type === "documents") setDocuments(result.data.documents || []);
          if (result.type === "bank") setBankAccount(result.data.bankAccount || null);
        });
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || "Unable to reveal secure employee details.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [accessToken, canViewBank, canViewDocuments, employee]);

  if (!employee) return null;

  const openDocument = (storageKey) => {
    if (!storageKey) return;
    const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3000").replace(/\/$/, "");
    window.open(`${baseUrl}${storageKey}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[110] overflow-y-auto bg-gray-950/60 p-4 backdrop-blur-sm sm:p-6">
      <div className="mx-auto flex min-h-full max-w-4xl items-center">
        <section className="w-full overflow-hidden rounded-3xl bg-white shadow-2xl">
          <header className="flex items-start justify-between border-b border-gray-100 p-6">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary-50 text-primary-800">
                <FiLock size={22} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Restricted HRMS information</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900">Sensitive employee details</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {employee.fullName} · {employee.employeeCode}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-xl text-gray-500 hover:bg-gray-100" aria-label="Close secure details">
              <FiX size={20} />
            </button>
          </header>

          <div className="p-6">
            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              These values are decrypted only for this authorised view. Do not copy, share, or retain them outside an approved HR process.
            </div>

            {error && <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            {loading ? (
              <div className="py-16 text-center text-sm text-gray-500">Loading protected information...</div>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                {canViewBank && (
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-bold text-gray-900">Bank details</h3>
                    {bankAccount ? (
                      <dl className="mt-5 space-y-4 text-sm">
                        <div><dt className="text-gray-500">Bank name</dt><dd className="mt-1 font-semibold text-gray-900">{bankAccount.bankName || "—"}</dd></div>
                        <div><dt className="text-gray-500">Account holder</dt><dd className="mt-1 font-semibold text-gray-900">{bankAccount.accountHolderName || "—"}</dd></div>
                        <div><dt className="text-gray-500">Account number</dt><dd className="mt-1 break-all font-mono font-semibold text-gray-900">{bankAccount.accountNumber || "Not recorded"}</dd></div>
                        <div><dt className="text-gray-500">IFSC code</dt><dd className="mt-1 font-mono font-semibold text-gray-900">{bankAccount.ifscCode || "Not recorded"}</dd></div>
                      </dl>
                    ) : <p className="mt-5 text-sm text-gray-500">No bank details are recorded.</p>}
                  </section>
                )}

                {canViewDocuments && (
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-bold text-gray-900">Identity documents</h3>
                    <div className="mt-5 space-y-3">
                      {documents.length ? documents.map((document) => (
                        <div key={document.id} className="rounded-xl bg-gray-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-gray-900">{title(document.documentType)}</p>
                              <p className="mt-1 break-all font-mono text-sm text-gray-700">{document.documentNumber || "Number not recorded"}</p>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(document.status)}`}>{title(document.status)}</span>
                          </div>
                          {document.storageKey && (
                            <button type="button" onClick={() => openDocument(document.storageKey)} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary-800 hover:text-primary-900">
                              <FiDownload /> View or download {document.originalName || "file"}
                            </button>
                          )}
                        </div>
                      )) : <p className="text-sm text-gray-500">No identity documents are recorded.</p>}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>

          <footer className="flex justify-end border-t border-gray-100 p-5">
            <button type="button" onClick={onClose} className="h-11 rounded-xl border border-gray-200 px-5 font-medium text-gray-700 hover:bg-gray-50">Close</button>
          </footer>
        </section>
      </div>
    </div>
  );
}
